import React from 'react'

import { NeutralColorToken } from '../../themes'
import { IconButton } from '../IconButton'
import { TextButton } from '../TextButton'
import { Type } from '../Type'
import type { StepProps, TimelineOrientation, TimelineStatus } from './types'
import { SMALL_DEVICE_BREAKPOINT } from './utils'

type Props = {
  gridArea: string
  isResponsive: boolean
  orientation: TimelineOrientation
} & StepProps

export function StepLabel({
  additionalDescription,
  description,
  gridArea,
  info,
  infoAction,
  isResponsive,
  label,
  orientation,
  status,
  textButton,
}: Props) {
  const labelColor =
    status === 'incomplete' ? NeutralColorToken.Fg2 : NeutralColorToken.Fg1

  return (
    <div
      css={[
        {
          display: 'flex',
          flexDirection: 'column',
          gridArea,
          paddingBottom: 20,
        },
        getLabelStyles(orientation, status),
        isResponsive && {
          [`@media screen and (max-width: ${SMALL_DEVICE_BREAKPOINT}px)`]:
            getLabelStyles('vertical', status),
        },
      ]}
    >
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <Type.Text color={labelColor} maxLines={2} size="S">
          {label}
        </Type.Text>
        {info === 'label' && (
          <IconButton
            aria-label="Info"
            foregroundColor={labelColor}
            icon="Info16"
            onClick={infoAction}
            type="button"
          />
        )}
      </div>
      {description && (
        <div
          css={{
            alignItems: 'center',
            display: 'flex',
          }}
        >
          <Type.Text color={NeutralColorToken.Fg2} maxLines={1} size="S">
            {description}
          </Type.Text>
          {info === 'metadata' && (
            <IconButton
              aria-label="Info"
              foregroundColor={NeutralColorToken.Fg2}
              icon="Info16"
              onClick={infoAction}
              type="button"
            />
          )}
        </div>
      )}
      {additionalDescription && (
        <Type.Text color={NeutralColorToken.Fg2} maxLines={1} size="S">
          {additionalDescription}
        </Type.Text>
      )}
      {textButton && (
        <TextButton
          isLegacyStyle
          label={textButton.label}
          maxLines={1}
          onClick={textButton.onClick}
          overrideRootStyles={{
            marginTop: 4,
          }}
          type="button"
          variant="standalone"
        />
      )}
    </div>
  )
}

export function getLabelStyles(
  orientation: TimelineOrientation,
  status: TimelineStatus
):
  | {
      alignItems: 'flex-start' | 'center'
      marginTop: number | undefined
      paddingLeft: number | undefined
      paddingRight: number | undefined
      textAlign: 'center' | undefined
    }
  | undefined {
  switch (orientation) {
    case 'horizontal-left-align': {
      return {
        alignItems: 'flex-start',
        marginTop: undefined,
        paddingLeft: undefined,
        paddingRight: 12,
        textAlign: undefined,
      }
    }
    case 'horizontal-center-align': {
      return {
        alignItems: 'center',
        marginTop: undefined,
        paddingLeft: 6,
        paddingRight: 6,
        textAlign: 'center',
      }
    }
    case 'vertical': {
      return {
        alignItems: 'flex-start',
        // When we display a Dot instead of 24x24 px icon, add a negative margin so that label is aligned with icon.
        marginTop: status === 'incomplete' ? -4 : undefined,
        paddingLeft: undefined,
        paddingRight: undefined,
        textAlign: undefined,
      }
    }
    default: {
      return undefined
    }
  }
}
